import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * In-app navigation history stack.
 *
 * Stored at module scope so a single shared stack is maintained across all
 * consumers without needing a context provider. The stack holds React Router
 * location keys — each unique key represents a distinct history entry.
 *
 * Rules:
 *  - Forward navigation (new key): push onto the stack.
 *  - Back/forward navigation (previously-seen key): truncate the stack to
 *    that entry, discarding anything that was "forward" of it.
 *
 * Call resetInAppHistory() when the user logs in to ensure the first screen
 * after login never shows a stale back depth from a prior session.
 *
 * NOTE — Next.js/SSR: this module-scoped array is safe for the current
 * Create React App (client-side only) build. When BL-162 (Next.js migration)
 * lands, this hook must be migrated to a React context or per-render-tree
 * ref to avoid sharing state across server-side requests.
 */
const _stack = [];

export function resetInAppHistory() {
    _stack.length = 0;
}

export function useBackNavigation() {
    const navigate = useNavigate();
    const location = useLocation();
    // Initialise to the current stack length so remounts get the right depth
    // immediately; the useEffect will push the current key and sync it.
    const [depth, setDepth] = useState(_stack.length);

    useEffect(() => {
        const existingIdx = _stack.indexOf(location.key);
        if (existingIdx === -1) {
            _stack.push(location.key);
        } else {
            // Navigated back (or forward) to a previously-visited entry —
            // drop everything after it so the stack reflects the real position.
            _stack.splice(existingIdx + 1);
        }
        setDepth(_stack.length);
    }, [location.key]);

    // depth === 1 means we're on the first (or only) in-app entry — nothing to go back to.
    const canGoBack = depth > 1;

    const goBack = useCallback(() => {
        if (depth > 1) {
            navigate(-1);
        }
    }, [depth, navigate]);

    return { canGoBack, goBack };
}
